/**
 * FlowSession Provider
 * React context that holds the current bootstrap session and provides
 * session-related actions (complete, exit, expire) to all child components.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { i18nGlobal } from "@/i18n";
import type { InboundMessage } from "@/types/messaging.types";
import type { FlowSession, SessionStatus } from "@/types/session.types";
import { onAuthError } from "@api/clients";
import { STORAGE_KEYS } from "@constants/storage.constants";
import { isSessionExpired } from "@utils/bootstrap";
import {
  emitFlowCompleted,
  emitFlowExpired,
  emitFlowExited
} from "@utils/events";
import { loggerClient } from "@utils/logger";
import { onHostMessage } from "@utils/messaging";
import {
  setAuthTokenInStorage,
  setGroupKeyInStorage,
  clearAuthStorage
} from "@utils/storage";

import {
  FlowSessionContext,
  type FlowSessionContextValue
} from "../hooks/flow/useFlowSession";
import { detectEmbedded } from "../utils/flow-session.utils";

// Re-export useFlowSession so existing imports from this path keep working
export { useFlowSession } from "../hooks/flow/useFlowSession";

interface FlowSessionProviderProps {
  children: React.ReactNode;
  /** Called when a 401 auth error occurs so the router can navigate to session-expired */
  onSessionExpired?: () => void;
}

export const FlowSessionProvider: React.FC<FlowSessionProviderProps> = ({
  children,
  onSessionExpired
}) => {
  const [session, setSession] = useState<FlowSession | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEYS.FLOW_SESSION);
      if (!stored) return null;
      const restored = JSON.parse(stored) as FlowSession;
      // Don't restore expired sessions
      if (isSessionExpired(restored)) return null;
      loggerClient.setFlowType(restored.flow);
      return restored;
    } catch {
      return null;
    }
  });
  const [isEmbeddedMode] = useState(() => detectEmbedded());
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Hydrate storage with session credentials so API clients can use them */
  const hydrateStorage = useCallback((s: FlowSession) => {
    setAuthTokenInStorage(s.authToken);
    setGroupKeyInStorage(s.groupKey);
    sessionStorage.setItem(STORAGE_KEYS.DATASET_ID, s.datasetId);
    sessionStorage.setItem(STORAGE_KEYS.FLOW_SESSION, JSON.stringify(s));
  }, []);

  // Re-hydrate auth storage and expiry timer when session is restored from sessionStorage
  useEffect(() => {
    if (session && !expiryTimerRef.current) {
      hydrateStorage(session);
      const ttl = new Date(session.expiresAt).getTime() - Date.now();
      if (ttl > 0) {
        expiryTimerRef.current = globalThis.setTimeout(() => {
          setSession((prev) =>
            prev ? { ...prev, status: "expired" as SessionStatus } : null
          );
          emitFlowExpired(session.flow, session.sessionId);
        }, ttl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount — initSession handles subsequent updates

  const initSession = useCallback(
    (s: FlowSession) => {
      setSession(s);
      hydrateStorage(s);
      loggerClient.setFlowType(s.flow);

      // Clear any existing expiry timer before setting a new one to prevent
      // double FLOW_EXPIRED emissions when a session is restored from sessionStorage
      // and initSession is subsequently called.
      if (expiryTimerRef.current) {
        globalThis.clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }

      // Set up an expiry timer
      const ttl = new Date(s.expiresAt).getTime() - Date.now();
      if (ttl > 0) {
        expiryTimerRef.current = globalThis.setTimeout(() => {
          setSession((prev) =>
            prev ? { ...prev, status: "expired" as SessionStatus } : null
          );
          emitFlowExpired(s.flow, s.sessionId);
        }, ttl);
      }
    },
    [hydrateStorage]
  );

  const completeFlow = useCallback(
    (result?: Record<string, unknown>) => {
      if (!session) return;
      setSession((prev) => (prev ? { ...prev, status: "completed" } : null));
      emitFlowCompleted(session.flow, session.sessionId, result);
    },
    [session]
  );

  const exitFlow = useCallback(
    (reason: "user_cancelled" | "navigation" | "error" = "user_cancelled") => {
      if (!session) return;
      setSession((prev) => (prev ? { ...prev, status: "cancelled" } : null));
      emitFlowExited(session.flow, session.sessionId, reason);
    },
    [session]
  );

  const expireFlow = useCallback(() => {
    if (!session) return;
    setSession((prev) => (prev ? { ...prev, status: "expired" } : null));
    emitFlowExpired(session.flow, session.sessionId);
  }, [session]);

  // Listen for host messages (token refresh, close, etc.)
  useEffect(() => {
    const unsubscribe = onHostMessage((msg: InboundMessage) => {
      switch (msg.type) {
        case "TOKEN_REFRESH":
          if (session) {
            setAuthTokenInStorage(msg.payload.authToken);
            setSession((prev) =>
              prev ? { ...prev, authToken: msg.payload.authToken } : null
            );
          }
          break;
        case "CLOSE_FLOW":
          exitFlow("navigation");
          break;
        case "SESSION_UPDATE":
          if (session) {
            setSession((prev) =>
              prev ? { ...prev, status: msg.payload.status } : null
            );
          }
          break;
        case "LANGUAGE_CHANGE":
          if (msg.payload.language) {
            i18nGlobal.changeLanguage(msg.payload.language);
          }
          break;
      }
    });
    return unsubscribe;
  }, [session, exitFlow]);

  // Periodically check if session has expired (belt + suspenders with the timer)
  useEffect(() => {
    if (session?.status !== "active") return;

    const interval = globalThis.setInterval(() => {
      if (isSessionExpired(session)) {
        expireFlow();
      }
    }, 30_000); // Check every 30 seconds

    return () => globalThis.clearInterval(interval);
  }, [session, expireFlow]);

  // Listen for 401 auth errors from API clients → navigate to session-expired page
  useEffect(() => {
    const unsubscribe = onAuthError(() => {
      clearAuthStorage();
      onSessionExpired?.();
    });
    return unsubscribe;
  }, [onSessionExpired]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (expiryTimerRef.current) {
        globalThis.clearTimeout(expiryTimerRef.current);
      }
      clearAuthStorage();
    };
  }, []);

  const value = useMemo<FlowSessionContextValue>(
    () => ({
      session,
      isEmbeddedMode,
      setSession: initSession,
      completeFlow,
      exitFlow,
      expireFlow
    }),
    [session, isEmbeddedMode, initSession, completeFlow, exitFlow, expireFlow]
  );

  return (
    <FlowSessionContext.Provider value={value}>
      {children}
    </FlowSessionContext.Provider>
  );
};
